import { Box, Chip, Divider, LinearProgress, TextField, Typography } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import Cam from 'components/CardProductScanner/Cam'
import { usePopup } from 'hooks/usePopup'
import { useEffect, useMemo, useState } from 'react'
import {
  IRouteCapacityDetail,
  IRouteCapacityOrder,
  IRouteOverResponse,
  routeService,
} from 'services/routeService'

import { describeRouteOverflowCause } from '../../routeOverflowPresentation'
import {
  Button,
  ButtonBack,
  ButtonConfirm,
  CloseButton,
  Modal,
  OrderList,
  Paper,
  Subtitle,
  Text,
  Title,
} from './styles'

interface IModalScan {
  open: boolean
  onClose: () => void
  data: IRouteOverResponse
  refresh: () => Promise<void>
}

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString('pt-BR') : 'horário não informado'

const ModalScan = ({ open, onClose, data, refresh }: IModalScan) => {
  const { addPopup } = usePopup()
  const [detail, setDetail] = useState<IRouteCapacityDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [cameraOpen, setCameraOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<IRouteCapacityOrder | null>(null)
  const [boxesCount, setBoxesCount] = useState('')
  const [idempotencyKey, setIdempotencyKey] = useState('')
  const [repeatConfirmation, setRepeatConfirmation] = useState<any>(null)

  const loadDetail = async () => {
    try {
      setLoading(true)
      setDetail(await routeService.getRouteCapacity(data.route))
    } catch (err: any) {
      addPopup({
        title: 'Erro ao abrir a rota',
        description: err?.message ?? err?.msg ?? 'Erro desconhecido',
        type: 'error',
        autoClose: false,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDetail()
  }, [data.route])

  const filteredOrders = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return detail?.orders || []
    return (detail?.orders || []).filter(
      order =>
        order.order_id.toLowerCase().includes(term) ||
        (order.client_name_or_trade || '').toLowerCase().includes(term),
    )
  }, [detail, search])

  const chooseOrder = (order: IRouteCapacityOrder) => {
    setSelectedOrder(order)
    setBoxesCount(String(Math.max(1, order.boxes_count - 1)))
    setIdempotencyKey(`route-capacity:${data.route}:${order.order_id}:${Date.now()}`)
    setRepeatConfirmation(null)
    setCameraOpen(false)
  }

  const onScanQr = (qrCode: string) => {
    const orderId = qrCode.split(';')[0]?.trim().toLowerCase()
    const order = detail?.orders.find(item => item.order_id.toLowerCase() === orderId)
    if (!order) {
      addPopup({
        title: 'Pedido não encontrado nesta rota',
        description: `O QR Code ${qrCode} não pertence à rota ${data.route}.`,
        type: 'error',
      })
      return
    }
    setSearch(order.order_id)
    chooseOrder(order)
  }

  const reduceOrder = async (confirmAgain = false) => {
    if (!selectedOrder) return
    const requested = Number(boxesCount)
    if (!Number.isInteger(requested) || requested < 1 || requested >= selectedOrder.boxes_count) {
      addPopup({
        title: 'Quantidade inválida',
        description: `Informe um valor entre 1 e ${selectedOrder.boxes_count - 1}.`,
        type: 'error',
      })
      return
    }

    try {
      setLoading(true)
      const response = await routeService.reduceRouteOrder(data.route, selectedOrder.order_id, {
        boxes_count: requested,
        expected_version: selectedOrder.current_version,
        confirm_reduction_again: confirmAgain,
        idempotency_key: idempotencyKey,
      })
      setDetail(response)
      setSelectedOrder(null)
      setRepeatConfirmation(null)
      await refresh()
      addPopup({ title: `Pedido ${selectedOrder.order_id} reduzido com sucesso`, type: 'success' })
    } catch (err: any) {
      if (err?.code === 'repeat_reduction_confirmation_required') {
        setRepeatConfirmation(err)
        return
      }
      if (err?.code === 'stale_order_box_count') await loadDetail()
      addPopup({
        title: 'Erro ao reduzir o pedido',
        description: err?.message ?? err?.msg ?? 'Erro desconhecido',
        type: 'error',
        autoClose: false,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Paper>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mt="1rem">
          <Title>Rota {data.route}</Title>
          <CloseButton aria-label="Fechar tratamento da rota" onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          mb="2rem"
          style={{ gap: '.75rem' }}
        >
          <Subtitle style={{ marginBottom: 0 }}>
            {detail?.license_plate || 'Veículo não informado'} ·{' '}
            {detail?.boxes_count ?? data.real_boxes_number} /{' '}
            {detail?.capacity_vehicle ?? data.capacity_vehicle} caixas
          </Subtitle>
          <Chip
            label={`+${detail?.excess_boxes ?? data.excess_boxes} caixas`}
            style={{ color: '#9C1C1C', background: '#FDE2E2', fontWeight: 600 }}
          />
        </Box>

        {detail && (
          <Box padding="1rem" bgcolor="#FFF7E6" borderRadius="8px" mb="1rem">
            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
              O que fez a rota exceder a capacidade
            </Typography>
            <Typography variant="body2">
              {describeRouteOverflowCause(detail.incident, {
                boxesCount: detail.boxes_count,
                capacityVehicle: detail.capacity_vehicle,
              })}
            </Typography>
          </Box>
        )}

        {selectedOrder ? (
          <Box border="1px solid #E0E0E0" borderRadius="8px" padding="1rem" mb="1rem">
            <Typography variant="h6">Reduzir pedido {selectedOrder.order_id}</Typography>
            <Typography color="textSecondary">
              Quantidade atual: {selectedOrder.boxes_count} caixas. Zero não é uma redução válida.
            </Typography>
            <TextField
              type="number"
              variant="outlined"
              label="Quantidade após a redução"
              value={boxesCount}
              onChange={event => setBoxesCount(event.target.value)}
              inputProps={{ min: 1, max: selectedOrder.boxes_count - 1 }}
              fullWidth
              style={{ marginTop: '1rem' }}
            />
            {repeatConfirmation && (
              <Box bgcolor="#FDE2E2" padding="1rem" borderRadius="8px" mt="1rem">
                <Typography style={{ fontWeight: 600 }}>
                  Este pedido já foi reduzido neste incidente.
                </Typography>
                <Typography variant="body2">
                  Última redução: {repeatConfirmation.last_reduction?.boxes_count} caixas por{' '}
                  {repeatConfirmation.last_reduction?.created_by || 'usuário não informado'}, em{' '}
                  {formatDate(repeatConfirmation.last_reduction?.created_at)}. Confirme para reduzir
                  novamente.
                </Typography>
              </Box>
            )}
            <Box display="flex" flexWrap="wrap" style={{ gap: '.75rem' }} mt="1rem">
              <ButtonBack onClick={() => setSelectedOrder(null)}>Cancelar</ButtonBack>
              <ButtonConfirm onClick={() => reduceOrder(Boolean(repeatConfirmation))}>
                {repeatConfirmation ? 'Confirmar nova redução' : 'Confirmar redução'}
              </ButtonConfirm>
            </Box>
          </Box>
        ) : (
          <>
            <Box display="flex" flexWrap="wrap" style={{ gap: '.75rem' }} mb="1rem">
              <TextField
                variant="outlined"
                size="small"
                label="Buscar pedido ou cliente"
                value={search}
                onChange={event => setSearch(event.target.value)}
                style={{ flex: '1 1 240px' }}
              />
              <Button
                onClick={() => setCameraOpen(value => !value)}
                style={{ width: 'auto', marginTop: 0 }}
              >
                {cameraOpen ? 'Fechar câmera' : 'Encontrar por QR Code'}
              </Button>
            </Box>
            <LinearProgress style={{ width: '100%', opacity: loading ? 1 : 0 }} />
            {cameraOpen && (
              <Box mb="1rem">
                <Cam onSubmit={onScanQr} loading={loading} />
              </Box>
            )}
          </>
        )}

        {!selectedOrder && (
          <OrderList>
            {filteredOrders.map(order => (
              <Box key={order.order_id}>
                <Box
                  display="flex"
                  flexWrap="wrap"
                  justifyContent="space-between"
                  alignItems="center"
                  padding=".85rem 0"
                  style={{ gap: '.75rem' }}
                >
                  <Box flex="1 1 260px">
                    <Box display="flex" alignItems="center" style={{ gap: '.5rem' }}>
                      <Typography style={{ fontWeight: 600 }}>Pedido {order.order_id}</Typography>
                      {order.already_reduced && <Chip size="small" label="Já reduzido" />}
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      {order.client_name_or_trade || 'Cliente não informado'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Origem atual: {order.current_event?.source_label || 'legado sem evento'}
                    </Typography>
                  </Box>
                  <Typography style={{ fontWeight: 700 }}>{order.boxes_count} caixas</Typography>
                  <ButtonConfirm
                    disabled={order.boxes_count <= 1}
                    onClick={() => chooseOrder(order)}
                    style={{ marginTop: 0, padding: '0 1.25rem' }}
                  >
                    Reduzir
                  </ButtonConfirm>
                </Box>
                <Divider />
              </Box>
            ))}
            {detail && filteredOrders.length === 0 && <Text>Nenhum pedido encontrado.</Text>}
          </OrderList>
        )}

        {!selectedOrder && detail && filteredOrders.length !== detail.orders.length && (
          <Box mt="1rem">
            <Typography color="textSecondary">
              {filteredOrders.length} pedido(s) encontrado(s).
            </Typography>
          </Box>
        )}

        <Box mt="1.5rem">
          <Typography variant="caption" color="textSecondary">
            {detail?.incident?.status === 'resolved'
              ? 'A rota foi regularizada e não precisa de mais tratamento.'
              : 'A rota continuará pendente até que a quantidade de caixas fique dentro da capacidade.'}
          </Typography>
        </Box>
      </Paper>
    </Modal>
  )
}

export default ModalScan
